/**
 * MaterialAppBar - Material Design 3 Top App Bar
 * Mobile-first, app-like design optimized for low-tech users
 * 
 * Features:
 * - Fixed at top with Material elevation (4dp)
 * - Hamburger menu icon (opens drawer)
 * - Page title
 * - Current division/school name
 * - Optional action buttons (help, notifications)
 * - Status bar color matching
 */

import React from 'react';
import { Menu, ArrowLeft } from 'lucide-react';

import { useAuthContext } from '../../../contexts/AuthContext';
import NotificationBell from '../schedule/NotificationBell';

/**
 * MaterialAppBar Component
 * @param {Object} props
 * @param {string} props.title - Page title
 * @param {Function} props.onMenuClick - Callback when menu icon is clicked
 * @param {boolean} props.showNotifications - Show notification icon
 * @param {Function} props.onNotificationClick - Callback when notification icon is clicked
 * @param {React.ReactNode} props.children - Optional additional content (e.g., DivisionSwitcher)
 */
export default function MaterialAppBar({
  title = 'My Classes',
  onMenuClick,
  showBack = false, // New Prop
  onBackClick,      // New Prop
  showNotifications = false,
  unreadCount = 0,
  onRefreshCount,
  children,
  contextSwitcher,
  hideContext = false,
}) {
  const { activeAssignment } = useAuthContext();

  // Legacy context display (fallback if no contextSwitcher provided)
  const getCurrentContext = () => {
    if (!activeAssignment) return null;
    const school = activeAssignment.school?.name || '';
    const className = activeAssignment.class?.name || '';
    const divisionName = activeAssignment.division?.name || '';

    if (school && className && divisionName) {
      return `${school} - Class ${className}${divisionName ? ` ${divisionName}` : ''}`;
    }
    return null;
  };

  const currentContext = getCurrentContext();

  return (
    <header
      className="fixed top-0 left-0 right-0 z-app-bar bg-primary-500 text-white shadow-md-elevation-4"
      style={{
        height: '56px', // Standardized mobile app bar height (h-14)
      }}
    >
      <div className="flex items-center justify-between h-full px-lg">
        {/* Left: Menu OR Back Icon */}
        {showBack ? (
          <button
            onClick={onBackClick}
            className="flex items-center justify-center min-h-touch-min min-w-touch-min rounded-full hover:bg-white/10 active:bg-white/20 transition-colors"
            aria-label="Go back"
          >
            <ArrowLeft size={24} className="text-white" />
          </button>
        ) : (
          <button
            onClick={onMenuClick}
            className="flex items-center justify-center min-h-touch-min min-w-touch-min rounded-full hover:bg-white/10 active:bg-white/20 transition-colors"
            aria-label="Open menu"
            aria-expanded="false"
          >
            <Menu size={24} className="text-white" />
          </button>
        )}

        {/* Center: Title and Context OR Context Switcher */}
        <div className="flex-1 min-w-0 mx-2 md:mx-4">
          {contextSwitcher ? (
            // If custom switcher provided, render it
            <div className="w-full max-w-md">
              {contextSwitcher}
            </div>
          ) : (
            // Default Title + Subtitle behavior
            <>
              <h1 className="text-mobile-h1 font-bold text-white truncate">
                {String(title || '')}
              </h1>
              {currentContext && !hideContext && (
                <p className="text-mobile-body-sm text-white/90 truncate mt-xs">
                  {String(currentContext)}
                </p>
              )}
            </>
          )}
        </div>

        {/* Right: Action Buttons */}
        <div className="flex items-center gap-2">
          {/* Optional: Additional children (e.g., DivisionSwitcher for multiple assignments) */}
          {children && <div className="flex items-center">{children}</div>}

          {/* Notification Bell */}
          {showNotifications && (
            <NotificationBell count={unreadCount} onCountChange={onRefreshCount} />
          )}
        </div>
      </div>
    </header >
  );
}

